import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion, useMotionValue, useMotionTemplate, useSpring } from "framer-motion";
import emailjs from "@emailjs/browser";
import nousContacter from "../images/nousC.png";
import { PageMeta } from "../seo/PageMeta";



// ── Nav ──────────────────────────────────────────────────────────
function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const navLinks = [
      { label: "Accueil", href: "/" },
      { label: "Nos projets", href: "/projet" },
      { label: "Nous contacter", href: "/contact" },
      { label: "À propos", href: "/about" },
    ];

    useEffect(() => {
      const onScroll = () => setScrolled(window.scrollY > 20);
      window.addEventListener("scroll", onScroll);
      return () => window.removeEventListener("scroll", onScroll);
    }, []);
  
    return (
      <motion.nav
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-0 transition-all duration-500 ${
          scrolled ? "bg-white/80 backdrop-blur-md shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="mx-auto w-full max-w-6xl py-4 md:py-5">
          <div className="flex items-center justify-between md:hidden">
            <a href="/" className="text-sm font-semibold tracking-wide text-[#1a1a2e]">
              LMS
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="Ouvrir le menu"
              className="rounded-lg p-2 text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
            >
              <span className="block h-0.5 w-6 bg-current mb-1.5" />
              <span className="block h-0.5 w-6 bg-current mb-1.5" />
              <span className="block h-0.5 w-6 bg-current" />
            </button>
          </div>

          <div className="hidden md:flex justify-center gap-10">
            {navLinks.map(({ label, href }) => (
              <motion.a
                key={label}
                href={href}
                className="relative text-sm font-medium text-[#1a1a2e] tracking-wide"
                whileHover="hover"
                animate="rest"
              >
                {label}
                <motion.span
                  className="absolute -bottom-0.5 left-0 h-[1.5px] bg-[#1a1a2e] block"
                  variants={{
                    rest: { width: "0%" },
                    hover: { width: "100%" },
                  }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </motion.a>
            ))}
          </div>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="mt-3 rounded-2xl border border-[#1a1a2e]/10 bg-white/95 p-3 shadow-md md:hidden"
              >
                <div className="flex flex-col">
                  {navLinks.map(({ label, href }) => (
                    <a
                      key={label}
                      href={href}
                      onClick={() => setMenuOpen(false)}
                      className="rounded-lg px-3 py-2 text-sm font-medium text-[#1a1a2e] hover:bg-[#1a1a2e]/5"
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>
    );
  }

// ── Google Fonts loader ──────────────────────────────────────────
function FontLoader() {
    useEffect(() => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }, []);
    return null;
  }

const SYSTEM_TYPES = [
  "Site web",
  "Applications mobile",
  "E-commerce",
  "Maintenance",
  "Service de CRM",
  "Outils numériques sur mesure"
];

const TIMELINES = [
  "Rapidement",
  "Très rapidement",
  "Ce n'est pas pressant",
];

const MIN_FILL_TIME_MS = 4000;
const MAX_ATTEMPTS_PER_WINDOW = 3;
const ATTEMPT_WINDOW_MS = 10 * 60 * 1000;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,60}$/;
const SUSPICIOUS_CONTENT_REGEX = /(<script|javascript:|onerror=|onload=|data:text\/html|bit\.ly|tinyurl|free\s+money|urgent|mot\s+de\s+passe|password)/i;
const LINK_REGEX = /(?:https?:\/\/|www\.|ftp:\/\/)/i;

function containsLink(value) {
  return LINK_REGEX.test(value);
}

function sanitizeText(value) {
  return value.replace(/[<>]/g, "").replace(/\s+/g, " ").trim();
}

function getSubmissionAttempts() {
  try {
    const raw = localStorage.getItem("contact_form_attempts");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((timestamp) => Number.isFinite(timestamp));
  } catch {
    return [];
  }
}

function saveSubmissionAttempts(attempts) {
  try {
    localStorage.setItem("contact_form_attempts", JSON.stringify(attempts));
  } catch {
    // Ignore localStorage failures (private mode, disabled storage, etc.)
  }
}

function getRecentAttempts() {
  const now = Date.now();
  const attempts = getSubmissionAttempts();
  const recentAttempts = attempts.filter((timestamp) => now - timestamp <= ATTEMPT_WINDOW_MS);
  saveSubmissionAttempts(recentAttempts);
  return recentAttempts;
}

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();

  const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      if (window.turnstile) resolve();
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Impossible de charger Cloudflare Turnstile."));
    document.head.appendChild(script);
  });
}

function ContactForm() {
  const [formData, setFormData] = useState({
    email: "",
    nom: "",
    prenom: "",
    typeSysteme: "",
    description: "",
    delai: "",
    company: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isFormHovered, setIsFormHovered] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formOpenedAt] = useState(() => Date.now());
  const formBorderRef = useRef(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const rawX = useMotionValue(-1);
  const rawY = useMotionValue(-1);
  const mouseX = useSpring(rawX, { stiffness: 110, damping: 22, mass: 0.4 });
  const mouseY = useSpring(rawY, { stiffness: 110, damping: 22, mass: 0.4 });

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return undefined;

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current) return;
      if (turnstileWidgetId.current != null) return;

      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        size: "flexible",
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };

    loadTurnstileScript()
      .then(renderWidget)
      .catch(() => {
        if (!cancelled) {
          setError("Impossible de charger la vérification de sécurité.");
        }
      });

    return () => {
      cancelled = true;
      if (turnstileWidgetId.current != null && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const handleFormMouseMove = (e) => {
    if (!formBorderRef.current) return;
    const rect = formBorderRef.current.getBoundingClientRect();
    rawX.set(e.clientX - rect.left);
    rawY.set(e.clientY - rect.top);
  };

  const handleFormMouseLeave = () => {
    setIsFormHovered(false);
    if (!formBorderRef.current) return;
    const rect = formBorderRef.current.getBoundingClientRect();
    rawX.set(rect.width / 2);
    rawY.set(rect.height + 80);
  };

  const animatedBorder = useMotionTemplate`radial-gradient(
    320px circle at ${mouseX}px ${mouseY}px,
    rgba(79, 158, 207, 1),
    rgba(79, 158, 207, 0.58) 38%,
    rgba(79, 158, 207, 0.22) 64%,
    rgba(79, 158, 207, 0) 100%
  )`;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validateForm = (data) => {
    const sanitizedData = {
      email: data.email.trim().toLowerCase(),
      nom: sanitizeText(data.nom),
      prenom: sanitizeText(data.prenom),
      typeSysteme: data.typeSysteme.trim(),
      description: sanitizeText(data.description),
      delai: data.delai.trim(),
      company: data.company.trim(),
    };

    const hasSuspiciousContent =
      SUSPICIOUS_CONTENT_REGEX.test(sanitizedData.description) ||
      SUSPICIOUS_CONTENT_REGEX.test(sanitizedData.nom) ||
      SUSPICIOUS_CONTENT_REGEX.test(sanitizedData.prenom);

    const nextFieldErrors = {
      email: !sanitizedData.email || !EMAIL_REGEX.test(sanitizedData.email),
      nom:
        !sanitizedData.nom ||
        !NAME_REGEX.test(sanitizedData.nom) ||
        containsLink(sanitizedData.nom),
      prenom:
        !sanitizedData.prenom ||
        !NAME_REGEX.test(sanitizedData.prenom) ||
        containsLink(sanitizedData.prenom),
      typeSysteme: !sanitizedData.typeSysteme || !SYSTEM_TYPES.includes(sanitizedData.typeSysteme),
      description:
        !sanitizedData.description ||
        sanitizedData.description.length < 10 ||
        sanitizedData.description.length > 1200 ||
        containsLink(sanitizedData.description) ||
        hasSuspiciousContent,
      delai: !sanitizedData.delai || !TIMELINES.includes(sanitizedData.delai),
    };

    return { sanitizedData, nextFieldErrors };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const timeSinceOpened = Date.now() - formOpenedAt;
    if (timeSinceOpened < MIN_FILL_TIME_MS) {
      setError("Veuillez prendre quelques secondes pour remplir le formulaire.");
      setSuccess("");
      return;
    }

    // Honeypot: if hidden field is filled, likely bot traffic.
    if (formData.company.trim()) {
      setError("Requête non valide.");
      setSuccess("");
      return;
    }

    const attempts = getRecentAttempts();
    if (attempts.length >= MAX_ATTEMPTS_PER_WINDOW) {
      setError("Trop de tentatives. Veuillez réessayer dans quelques minutes.");
      setSuccess("");
      return;
    }

    const { sanitizedData, nextFieldErrors } = validateForm(formData);

    setFieldErrors(nextFieldErrors);

    if (Object.values(nextFieldErrors).some(Boolean)) {
      setError("Veuillez corriger les champs invalides avant l'envoi.");
      setSuccess("");
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Veuillez compléter la vérification de sécurité.");
      setSuccess("");
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setError("Configuration EmailJS manquante (service ID, template ID, public key).");
      setSuccess("");
      return;
    }

    setError("");
    setSuccess("");
    setIsSending(true);

    try {
      saveSubmissionAttempts([...attempts, Date.now()]);
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_email: sanitizedData.email,
          from_name: `${sanitizedData.prenom} ${sanitizedData.nom}`,
          prenom: sanitizedData.prenom,
          nom: sanitizedData.nom,
          email: sanitizedData.email,
          type_systeme: sanitizedData.typeSysteme,
          delai: sanitizedData.delai,
          description: sanitizedData.description,
          "cf-turnstile-response": turnstileToken,
        },
        { publicKey }
      );

      setSuccess("Merci! Votre demande a été envoyée.");
      setFieldErrors({});
      setFormData({
        email: "",
        nom: "",
        prenom: "",
        typeSysteme: "",
        description: "",
        delai: "",
        company: "",
      });
      setTurnstileToken("");
      if (turnstileWidgetId.current != null && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } catch (sendError) {
      setError("Impossible d'envoyer le message pour le moment. Veuillez réessayer.");
      setSuccess("");
      setTurnstileToken("");
      if (turnstileWidgetId.current != null && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setIsSending(false);
    }
  };

  const boxClasses =
    "rounded-xl border px-4 py-3 text-sm font-medium transition-all duration-200";

  return (
    <section
      className="min-h-screen pt-36 pb-32 px-6"
      style={{ background: "linear-gradient(180deg, #F8FBFF 0%, #DCEEFF 100%)" }}
    >
      <div className="max-w-3xl mx-auto">
        <div className="mx-auto w-fit text-center">
            <img className="mb-6 mx-auto block" src={nousContacter} alt="Nous contacter" height={400} width={400} />

            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.08 }}
                className="text-[#1a1a2e]/70 mb-10"
            >
            Parlez-nous de votre projet et on vous répond rapidement.
            </motion.p>
        </div>
        <motion.div
          ref={formBorderRef}
          onMouseMove={handleFormMouseMove}
          onMouseEnter={() => setIsFormHovered(true)}
          onMouseLeave={handleFormMouseLeave}
          className="rounded-2xl p-[2px] transition-shadow duration-300"
          style={{
            background: isFormHovered ? animatedBorder : "rgba(79, 158, 207, 0.45)",
            boxShadow: isFormHovered
              ? "0px 12px 38px rgba(79, 158, 207, 0.3)"
              : "0px 10px 26px rgba(12, 25, 37, 0.14)",
          }}
        >
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-6 md:p-8 space-y-6"
          >
            <input
              type="text"
              name="company"
              data-testid="contact-honeypot"
              value={formData.company}
              onChange={(e) => handleChange("company", e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                type="text"
                placeholder="Prénom"
                value={formData.prenom}
                onChange={(e) => handleChange("prenom", e.target.value)}
                className={`rounded-xl border px-4 py-3 outline-none focus:ring-2 focus:ring-[#4F9ECF] ${
                  fieldErrors.prenom ? "border-red-500" : "border-[#1a1a2e]/15"
                }`}
                />
                <input
                type="text"
                placeholder="Nom"
                value={formData.nom}
                onChange={(e) => handleChange("nom", e.target.value)}
                className={`rounded-xl border px-4 py-3 outline-none focus:ring-2 focus:ring-[#4F9ECF] ${
                  fieldErrors.nom ? "border-red-500" : "border-[#1a1a2e]/15"
                }`}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 -mt-2">
              <p className={`text-red-600 text-sm ${fieldErrors.prenom ? "block" : "hidden"}`}>
                {containsLink(formData.prenom)
                  ? "Les liens internet (http, https, www…) ne sont pas autorisés."
                  : "Le prénom est requis."}
              </p>
              <p className={`text-red-600 text-sm ${fieldErrors.nom ? "block" : "hidden"}`}>
                {containsLink(formData.nom)
                  ? "Les liens internet (http, https, www…) ne sont pas autorisés."
                  : "Le nom est requis."}
              </p>
            </div>

            <input
                type="email"
                placeholder="Adresse courriel"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className={`w-full rounded-xl border px-4 py-3 outline-none focus:ring-2 focus:ring-[#4F9ECF] ${
                  fieldErrors.email ? "border-red-500" : "border-[#1a1a2e]/15"
                }`}
            />
            <p className={`text-red-600 text-sm -mt-2 ${fieldErrors.email ? "block" : "hidden"}`}>
              Entrez une adresse courriel valide.
            </p>

            <div>
                <p className="text-sm font-semibold text-[#1a1a2e] mb-3">
                Type de système
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SYSTEM_TYPES.map((type) => {
                    const selected = formData.typeSysteme === type;
                    return (
                    <button
                        key={type}
                        type="button"
                        onClick={() => handleChange("typeSysteme", type)}
                        className={`${boxClasses} ${
                        selected
                            ? "border-[#4F9ECF] bg-[#4F9ECF] text-white"
                            : "border-[#1a1a2e]/20 text-[#1a1a2e] hover:border-[#4F9ECF]"
                        }`}
                    >
                        {type}
                    </button>
                    );
                })}
                </div>
                <p className={`text-red-600 text-sm mt-2 ${fieldErrors.typeSysteme ? "block" : "hidden"}`}>
                  Veuillez choisir un type de système.
                </p>
            </div>

            <div>
                <p className="text-sm font-semibold text-[#1a1a2e] mb-3">
                    Quand est-ce que vous en avez besoin ?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {TIMELINES.map((timeline) => {
                    const selected = formData.delai === timeline;
                    return (
                    <button
                        key={timeline}
                        type="button"
                        onClick={() => handleChange("delai", timeline)}
                        className={`${boxClasses} ${
                        selected
                            ? "border-[#0C1925] bg-[#0C1925] text-white"
                            : "border-[#1a1a2e]/20 text-[#1a1a2e] hover:border-[#0C1925]"
                        }`}
                    >
                        {timeline}
                    </button>
                    );
                })}
                </div>
                <p className={`text-red-600 text-sm mt-2 ${fieldErrors.delai ? "block" : "hidden"}`}>
                  Veuillez choisir un délai.
                </p>
            </div>

            <textarea
                placeholder="Description de votre demande"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                rows={5}
                className={`w-full rounded-xl border px-4 py-3 outline-none focus:ring-2 focus:ring-[#4F9ECF] resize-y ${
                  fieldErrors.description ? "border-red-500" : "border-[#1a1a2e]/15"
                }`}
            />
            <p className={`text-red-600 text-sm -mt-2 ${fieldErrors.description ? "block" : "hidden"}`}>
              {containsLink(formData.description)
                ? "Les liens internet (http, https, www…) ne sont pas autorisés."
                : "Décrivez votre projet (10-1200 caractères). Les liens internet ne sont pas autorisés."}
            </p>

            {TURNSTILE_SITE_KEY && (
              <div
                ref={turnstileRef}
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                data-theme="light"
                data-size="flexible"
              />
            )}

            {error && <p className="text-red-600 text-sm">{error}</p>}
            {success && <p className="text-green-600 text-md">{success}</p>}

            <button
                type="submit"
                disabled={isSending}
                className="w-full rounded-xl bg-[#0C1925] text-white py-3 font-semibold hover:bg-[#13283a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {isSending ? "Envoi en cours..." : "Envoyer la demande"}
            </button>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      className="bg-[#0C1925] border-t border-white/10 px-6 py-6 md:px-10"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-4 md:flex-row">
        <p className="text-center text-sm text-white/40 md:text-left">
          © {new Date().getFullYear()} LMS Technologies. Tous droits réservés.
        </p>

        <div className="flex w-full items-center justify-center gap-5 text-center text-sm lg:w-auto lg:justify-start lg:text-left">
          <a href="/condition" className="text-white/50 transition-colors hover:text-white">
            Conditions d&apos;utilisation
          </a>
          <span className="text-white/20">|</span>
          <a href="/confidentiality" className="text-white/50 transition-colors hover:text-white">
            Politique de confidentialite
          </a>
        </div>

        {/* Réseaux sociaux */}
        <div className="flex items-center gap-6">
        {[
          {
            label: "Instagram",
            href: "https://instagram.com",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
              </svg>
            ),
          },
          {
            label: "Facebook",
            href: "https://facebook.com",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            ),
          },
        ].map(({ label, href, icon }) => (
          <motion.a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="text-white/40 hover:text-white transition-colors"
            whileHover={{ scale: 1.2, y: -2 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            {icon}
          </motion.a>
        ))}
        </div>
      </div>
    </footer>
  );
}

export default function Contact() {
    return (
      <>
        <PageMeta
          title="Nous contacter"
          description="Parlez-nous de votre projet : site web, application mobile, e-commerce ou outil sur mesure. Nous vous répondons rapidement."
          path="/contact"
        />
        <FontLoader />
        <Navbar />
        <main style={{ fontFamily: "'Inter', sans-serif" }}>
          <ContactForm />
        </main>
        <Footer/>
      </>
    );
  }